import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Users, FileText, CreditCard, Calendar, TrendingUp, Zap, Smartphone } from 'lucide-react';

const IndependentAgentPricing = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Client Management',
      description: 'Complete CRM with client profiles, contact history, and bulk import'
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'Quote Builder',
      description: '17+ travel components including Disney, Universal, cruises, and more'
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: 'Payment Processing',
      description: 'Secure payment links, reminders, and commission tracking'
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: 'Itinerary Management',
      description: 'Convert quotes to itineraries and manage travel bookings'
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Analytics & Reports',
      description: 'Track performance, revenue, and client engagement'
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Workflow Automation',
      description: 'Custom stages, automated tasks, and email templates'
    },
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: 'Mobile Access',
      description: 'Manage your business from anywhere, on any device'
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: 'Professional Tools',
      description: 'AI-powered assistance, calendar integration, and more'
    }
  ];

  const handlePlanSelect = (plan) => {
    navigate(`/register/independent/signup?plan=${plan}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-blue-600">Travel+ CRM</h1>
            <button
              onClick={() => navigate('/independent/login')}
              className="text-gray-600 hover:text-gray-900 font-medium"
            >
              Sign In
            </button>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
          Start Your Independent Travel Agent Journey
        </h2>
        <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
          Full-featured CRM built specifically for independent travel agents. 
          Everything you need to manage clients, create quotes, and grow your business.
        </p>
      </div>

      {/* Features Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <h3 className="text-2xl font-bold text-gray-900 text-center mb-12">
          Everything You Need to Succeed
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="text-blue-600 mb-4">{feature.icon}</div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h4>
              <p className="text-gray-600 text-sm">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <h3 className="text-3xl font-bold text-gray-900 text-center mb-4">
          Simple, Transparent Pricing
        </h3>
        <p className="text-lg text-gray-600 text-center mb-12">
          Choose the plan that works best for you. All features included.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Monthly Plan */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-200 hover:border-blue-500 transition-all">
            <div className="text-center mb-6">
              <h4 className="text-2xl font-bold text-gray-900 mb-2">Monthly Plan</h4>
              <div className="flex items-baseline justify-center mb-4">
                <span className="text-5xl font-extrabold text-gray-900">$14.99</span>
                <span className="text-xl text-gray-600 ml-2">/month</span>
              </div>
              <p className="text-gray-600">Billed monthly</p>
            </div>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">All features included</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Unlimited clients & quotes</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Payment processing</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Cancel anytime</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">Email & chat support</span>
              </li>
            </ul>

            <button
              onClick={() => handlePlanSelect('monthly')}
              className="w-full bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Get Started
            </button>
          </div>

          {/* Yearly Plan - Featured */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl shadow-xl p-8 border-2 border-blue-600 relative transform md:scale-105">
            <div className="absolute top-0 right-0 bg-yellow-400 text-gray-900 font-bold px-4 py-1 rounded-bl-lg rounded-tr-lg text-sm">
              SAVE 20%
            </div>

            <div className="text-center mb-6">
              <h4 className="text-2xl font-bold text-white mb-2">Yearly Plan</h4>
              <div className="flex items-baseline justify-center mb-2">
                <span className="text-5xl font-extrabold text-white">$11.99</span>
                <span className="text-xl text-blue-100 ml-2">/month</span>
              </div>
              <p className="text-blue-100 mb-2">Billed annually at $143.88</p>
              <div className="inline-block bg-blue-500 bg-opacity-50 rounded-full px-4 py-1">
                <span className="text-white font-semibold">Save $35.88 per year!</span>
              </div>
            </div>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-white">All features included</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-white">Unlimited clients & quotes</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-white">Payment processing</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-white">Priority support</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-white">Best value - 2 months free!</span>
              </li>
            </ul>

            <button
              onClick={() => handlePlanSelect('yearly')}
              className="w-full bg-white text-blue-600 font-semibold py-3 px-6 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Get Started - Best Value
            </button>
          </div>
        </div>

        {/* Money Back Guarantee */}
        <div className="text-center mt-12">
          <p className="text-gray-600">
            🛡️ 30-day money-back guarantee • No setup fees • Cancel anytime
          </p>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h3>
          <div className="space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h4 className="font-semibold text-gray-900 mb-2">What's included in the subscription?</h4>
              <p className="text-gray-600">
                Everything! Full access to client management, quote builder with 17+ travel components, 
                payment processing, itinerary management, analytics, workflow automation, and all future updates.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h4 className="font-semibold text-gray-900 mb-2">Can I switch between plans?</h4>
              <p className="text-gray-600">
                Yes! You can upgrade from monthly to yearly at any time to start saving. 
                Downgrades take effect at the end of your current billing period.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h4 className="font-semibold text-gray-900 mb-2">Is there a setup fee or contract?</h4>
              <p className="text-gray-600">
                No setup fees and no long-term contracts. You can cancel anytime with no penalties.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h4 className="font-semibold text-gray-900 mb-2">What payment methods do you accept?</h4>
              <p className="text-gray-600">
                We accept all major credit cards (Visa, Mastercard, American Express, Discover) 
                processed securely through Stripe.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-600">
            <p>&copy; 2026 Travel+ CRM. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="/terms" className="hover:text-gray-900">Terms & Conditions</a>
              <a href="/privacy" className="hover:text-gray-900">Privacy Policy</a>
              <a href="/contact" className="hover:text-gray-900">Contact</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndependentAgentPricing;
